// ZodiacPersonalityTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserCircle } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  AstriaMexicoFieldBlock,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import { MXTextInput, MXSubmitButton } from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoZodiacPersonalityData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Zodiac Personality";

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoZodiacPersonalityData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<UserCircle className="w-5 h-5" />}
          title={t("mx.tabs.personality")}
          subtitle={t("mx.personality.subtitle")}
          shareText={`${data.core_trait}\n\n${data.closing}`}
        >
          <AstriaMexicoFieldBlock label={t("mx.field.coreTrait")} value={data.core_trait} />
          <AstriaMexicoFieldBlock label={t("mx.field.everydayStyle")} value={data.everyday_style} />
          <AstriaMexicoFieldBlock label={t("mx.field.whereItServes")} value={data.where_it_serves_you} />
          <AstriaMexicoFieldBlock label={t("mx.field.whereItTrips")} value={data.where_it_trips_you_up} />
          <AstriaMexicoFieldBlock label={t("mx.field.closing")} value={data.closing} />
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("mx.common.startNewReading")}
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<UserCircle className="w-5 h-5" />} title={t("mx.tabs.personality")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, onSubmit }) => {
  const { t } = useTranslation();
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("mx.tabs.personality")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("mx.personality.intro")}
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <MXTextInput value={dob} onChange={setDob} placeholder={t("mx.common.dob")} />
      </div>

      <MXSubmitButton onClick={onSubmit} disabled={!dob.trim()} label={t("mx.submit.personality")} />
    </>
  );
};

const ZodiacPersonalityTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(`Dame mi lectura de Personalidad. Mi fecha de nacimiento: ${dob.trim()}.`);

  const data = message?.astriaMexicoData as AstriaMexicoZodiacPersonalityData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="zodiac-personality">
        {loading && <MXLoadingState label={t("mx.loading.personality")} />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="zodiac-personality">
      <IntroForm dob={dob} setDob={setDob} onSubmit={handleSubmit} />
    </AstriaMexicoShell>
  );
};

export default ZodiacPersonalityTab;
